(function () {
  const INSURANCE_ID = /** @type {HTMLScriptElement|null} */ (
    document.currentScript || document.querySelector('script[src*="cart-insurance"]')
  )?.dataset?.insuranceId;

  const originalFetch = window.fetch.bind(window);
  const STORAGE_KEY = 'removeInsurance';

  // sessionStorage persists across page loads within the tab but resets when the
  // session ends, so returning visitors always get a fresh auto-add opportunity.
  // The in-memory variable is the runtime source of truth so section morphing
  // can never accidentally reset it by re-executing this script.
  let optedOut = sessionStorage.getItem(STORAGE_KEY) === 'true';

  function getInsuranceId() {
    return (
      /** @type {HTMLInputElement|null} */ (document.querySelector('input[name="insurance"]'))?.value ||
      INSURANCE_ID
    );
  }

  /** @param {string} variantId */
  async function addInsurance(variantId) {
    await originalFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    });
  }

  /** @param {string} variantId */
  async function removeInsurance(variantId) {
    const cart = await originalFetch('/cart.js').then((/** @type {Response} */ r) => r.json());
    const item = cart.items.find((/** @type {{ variant_id: number }} */ i) => i.variant_id === Number(variantId));
    if (!item) return;
    await originalFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 }),
    });
  }

  function triggerCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));
  }

  /**
   * @param {BodyInit | null | undefined} rawBody
   * @param {string} insuranceId
   */
  function isInsuranceBody(rawBody, insuranceId) {
    if (typeof rawBody === 'string' && rawBody.length > 0) {
      try {
        const parsed = JSON.parse(rawBody);
        const ids = Array.isArray(parsed.items)
          ? parsed.items.map((/** @type {{ id: number }} */ i) => i.id)
          : [parsed.id];
        return ids.map(Number).includes(Number(insuranceId));
      } catch {}
    } else if (rawBody instanceof FormData) {
      return Number(rawBody.get('id')) === Number(insuranceId);
    }
    return false;
  }

  let isAutoAdding = false;

  /** @param {BodyInit | null | undefined} rawBody */
  async function maybeAutoAdd(rawBody) {
    if (isAutoAdding || optedOut) return;

    const insuranceId = getInsuranceId();
    if (!insuranceId || isInsuranceBody(rawBody, insuranceId)) return;

    const checkbox = /** @type {HTMLInputElement|null} */ (document.querySelector('input[name="insurance"]'));
    if (checkbox?.checked) return;

    isAutoAdding = true;
    try {
      const cart = await originalFetch('/cart.js').then((/** @type {Response} */ r) => r.json());
      if (!cart.items.some((/** @type {{ variant_id: number }} */ i) => i.variant_id === Number(insuranceId))) {
        await addInsurance(insuranceId);
        triggerCartRefresh();
      }
    } finally {
      isAutoAdding = false;
    }
  }

  // Manual checkbox toggle — syncs user intent to localStorage and in-memory flag.
  document.addEventListener('change', async (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement) || checkbox.name !== 'insurance') return;

    checkbox.disabled = true;
    try {
      if (checkbox.checked) {
        await addInsurance(checkbox.value);
        optedOut = false;
        sessionStorage.setItem(STORAGE_KEY, 'false');
      } else {
        await removeInsurance(checkbox.value);
        optedOut = true;
        sessionStorage.setItem(STORAGE_KEY, 'true');
      }
      triggerCartRefresh();
    } finally {
      checkbox.disabled = false;
    }
  });

  // Intercept fetch — fire-and-forget, never blocks the add-to-cart response.
  window.fetch = async function (...args) {
    const [resource, options] = args;
    const response = await originalFetch(...args);

    const url = resource instanceof Request ? resource.url : String(resource);
    if (/\/cart\/add(\.js)?(\?|$)/.test(url) && response.ok) {
      maybeAutoAdd(options?.body).catch(() => {});
    }

    return response;
  };
})();
